import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { format, parseISO } from 'date-fns'

import { Tab, Tabs } from 'components/common/Tabs'

// 由後端完成排序，前端僅依 API 回傳順序進行年份分組。
const groupAchievementsByYear = (achievements = []) => {
  const groups = achievements.reduce((result, item) => {
    const year = parseISO(item.date).getFullYear()
    if (!result.has(year)) result.set(year, { year, items: [] })
    result.get(year).items.push(item)
    return result
  }, new Map())

  return Array.from(groups.values())
}

const Achievements = ({ achievements, onImageOpen }) => {
  const groups = groupAchievementsByYear(achievements)
  const [selectedYear, setSelectedYear] = useState(groups[0]?.year || false)
  const activeYear = groups.some(({ year }) => year === selectedYear)
    ? selectedYear
    : groups[0]?.year || false

  if (!groups.length) return null

  return (
    <Box component="section" mb={18}>
      <Typography component="h2" variant="h5" mb={2}>
        成就經歷
      </Typography>

      <Tabs
        value={activeYear}
        onChange={(_, value) => setSelectedYear(value)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="成就經歷年份"
        TabIndicatorProps={{
          children: <span className="MuiTabs-indicatorSpan" />,
        }}
        sx={(theme) => ({
          mb: 6,
          minHeight: 'unset',
          '& .MuiTabs-flexContainer': {
            gap: '28px',
          },
          '& .MuiTabs-indicator': {
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'transparent',
          },
          '& .MuiTabs-indicatorSpan': {
            width: '32px',
            borderRadius: '4px',
            backgroundColor: theme.palette.primary.main,
          },
          '&& .MuiTab-root': {
            ...theme.typography.titleSmall,
            minHeight: 'unset',
            padding: '14px 0',
            lineHeight: 1,
            letterSpacing: 0,
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
        })}
      >
        {groups.map(({ year }) => (
          <Tab
            key={year}
            id={`achievement-tab-${year}`}
            aria-controls={`achievement-panel-${year}`}
            value={year}
            label={String(year)}
          />
        ))}
      </Tabs>

      {groups.map(({ year, items }) => (
        <Box
          key={year}
          id={`achievement-panel-${year}`}
          role="tabpanel"
          aria-labelledby={`achievement-tab-${year}`}
          hidden={year !== activeYear}
          px={{ xs: 0, md: 4, lg: 6, xl: 8 }}
        >
          <Box display="flex" flexDirection="column" gap={4}>
            {items.map((item) => (
              <Box
                key={item._id}
                display="flex"
                alignItems="flex-start"
                gap={4}
              >
                <Typography
                  component="time"
                  dateTime={format(parseISO(item.date), 'yyyy-MM-dd')}
                  flex="0 0 48px"
                  variant="paraBodyMedium"
                  color="text.minus2"
                >
                  {format(parseISO(item.date), 'MM/dd')}
                </Typography>
                <Box flex={1} minWidth={0}>
                  <Typography
                    component="span"
                    variant="paraBodyMedium"
                    color="text.minus2"
                    sx={{
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {item.description}
                  </Typography>
                  {item?.image?.url && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={
                        <Box
                          component="img"
                          src="/img/ic_image.svg"
                          alt=""
                          width="16px"
                          height="16px"
                        />
                      }
                      onClick={() =>
                        onImageOpen?.({
                          src: item.image.url,
                          alt: item.description,
                        })
                      }
                      sx={{
                        ml: 1,
                        px: 2,
                        height: '24px',
                        verticalAlign: 'top',
                        color: 'primary.mainPlus2',
                        '& .MuiButton-startIcon': {
                          m: 0,
                          mr: 1,
                        },
                      }}
                    >
                      開啟圖片
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default Achievements
